import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarDays,
  Clock,
  MapPin,
  Share2,
  Star,
  BadgeCheck,
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Link2,
  CheckCircle2,
} from "lucide-react";
import { useParams } from 'react-router-dom';
import { eventService } from '../../../shared/services/api';
import { mockEvents } from '../data/mockEvents';
import { getImageUrl } from '../../../utils/imageUtils';
import { markdownishToHtml } from '../../../shared/utils/richText';
import '../../../styles/pages/events2.css'
import SEO from '../../../shared/components/seo/SEO';

/**
 * MACS Etkinlik Sayfası – Plan A (arka plan beyaz, hero lacivert overlay, kartlar beyaz)
 * - Hızlı yayın çözümü: Tek `content` alanından "Hakkında" ve "Program"u otomatik ayırır.
 * - Program satırlarını HH:MM - Başlık tarzında parse eder; bulunamazsa sentetik akışa düşer.
 */





function formatDateOnly(dt) {
  try {
    const dateStr = dt && !dt.endsWith('Z') ? dt + 'Z' : dt;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dt;
    return new Intl.DateTimeFormat("tr-TR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "Europe/Istanbul",
    }).format(d);
  } catch (e) {
    return dt;
  }
}

function formatTimeOnly(dt) {
  try {
    const dateStr = dt && !dt.endsWith('Z') ? dt + 'Z' : dt;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Istanbul",
    }).format(d);
  } catch (e) {
    return '';
  }
}

function classNames(...arr) {
  return arr.filter(Boolean).join(" ");
}

const badgeVariants = {
  initial: { opacity: 0, y: -6 },
  animate: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 22 } },
};

const cardVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

// --- Hızlı Yayın: content içinden Hakkında ve Program'ı ayır ---
function splitContent(content = "") {
  const raw = String(content || "").trim();
  if (!raw) return { aboutHtml: "<p>Detaylı içerik yakında.</p>", programLines: [] };
  // "## Program" / "<h2>Program</h2>" / "PROGRAM:"
  const markers = [/^##\s*Program\b/im, /<h2[^>]*>\s*Program\s*<\/h2>/i, /^PROGRAM:?$/im];
  let idx = -1, m = null;
  for (const rx of markers) {
    m = raw.match(rx);
    if (m) { idx = m.index ?? -1; break; }
  }
  if (idx === -1) return { aboutHtml: mdLikeToHtml(raw), programLines: [] };

  const before = raw.slice(0, idx).trim();
  const after = raw.slice(idx + (m?.[0]?.length || 0)).trim();
  const programLines = after.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
  return { aboutHtml: mdLikeToHtml(before), programLines };
}

function mdLikeToHtml(text) {
  // "## Hakkında" başlığını at, kalanı escape edilmiş şekilde biçimlendir.
  const withoutHeading = String(text ?? "").replace(/^##\s*Hakkında\b\s*/im, "");
  return markdownishToHtml(withoutHeading);
}

function parseProgram(programLines, startDate) {
  const items = [];
  const base = new Date(startDate);
  for (const line of programLines) {
    // Desteklenen kalıplar: "- 09:30 - Açılış", "09:30 Açılış", "09:30-10:00 Atölye"
    const cleaned = line.replace(/^[-*]\s*/, "");
    const m = cleaned.match(/(\d{1,2}:\d{2})(?:\s*[–—-]\s*(\d{1,2}:\d{2}))?\s*[–—-]?\s*(.+)/);
    if (m) {
      const [, startT, endT, title] = m;
      const s = combineDateTime(base, startT);
      items.push({ t: s, title: (title || "").trim(), end: endT ? combineDateTime(base, endT) : null });
    }
  }
  return items;
}

function combineDateTime(date, hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date(date);
  d.setHours(h, m, 0, 0);
  return d;
}

function EventPageView({ event }) {
  const isPast = new Date(event.end_time || event.start_time) < new Date();
  const { aboutHtml, programLines } = splitContent(event.content);

  return (
    <>
    <SEO
      title={event.title}
      description={(event.description || '').slice(0, 160)}
      image={getImageUrl(event.image_url)}
      url={`https://macsclub.com.tr/etkinlikler/${event.slug || ''}`}
      type="article"
    />
    <div className="min-h-screen bg-white text-[#07132b]">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${getImageUrl(event.image_url)})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-[#07132b]/70" />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative mx-auto max-w-7xl px-4 sm:px-6 pt-16 pb-12 sm:pt-20 sm:pb-16 lg:pt-28 lg:pb-24 text-white"
        >
          <div className="mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-white/80">
            <ArrowLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <a href="/etkinlikler" className="hover:underline">Etkinliklere dön</a>
          </div>

          <div className="flex flex-col-reverse items-start gap-6 sm:gap-8 lg:grid lg:grid-cols-5 lg:gap-12">
            {/* Left: Title & Meta */}
            <div className="col-span-3 w-full">
              <div className="mb-3 sm:mb-4 flex flex-wrap items-center gap-2 sm:gap-3">
                <motion.span
                  variants={badgeVariants}
                  initial="initial"
                  animate="animate"
                  className={classNames(
                    "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs",
                    event.is_featured
                      ? "border-yellow-400/50 bg-yellow-400/20 text-yellow-200"
                      : "border-white/20 bg-white/20 text-white"
                  )}
                >
                  {event.is_featured ? <Star className="h-3.5 w-3.5" /> : <BadgeCheck className="h-3.5 w-3.5" />}
                  {event.is_featured ? "Öne Çıkan" : event.category?.name || "Etkinlik"}
                </motion.span>
                {isPast ? (
                  <span className="inline-flex items-center gap-2 rounded-full bg-red-500/20 px-3 py-1 text-xs text-red-200 ring-1 ring-red-400/40">
                    <Clock className="h-3.5 w-3.5" /> Kayıt Kapalı
                  </span>
                ) : event.is_active ? (
                  <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/20 px-3 py-1 text-xs text-white-900 ring-1 ring-white-400/40">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Kayıt Açık
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2 rounded-full bg-slate-200 px-3 py-1 text-xs text-slate-700 ring-1 ring-slate-400/40">
                    <Clock className="h-3.5 w-3.5" /> Yakında
                  </span>
                )}
              </div>

              <motion.h1
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight tracking-tight"
              >
                {event.title}
              </motion.h1>

              <p className="mt-3 sm:mt-4 max-w-2xl text-sm sm:text-base text-white/90">{event.description}</p>

              {/* Meta chips */}
              <div className="mt-4 sm:mt-6 flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                <MetaChip icon={CalendarDays} label={formatDateOnly(event.start_time)} />
                <MetaChip icon={Clock} label={formatTimeOnly(event.start_time)} />
                <MetaChip icon={MapPin} label={event.location} />
              </div>

              {/* CTA */}
              <div className="mt-6 sm:mt-8 flex flex-wrap gap-2 sm:gap-3">
                {!isPast && (
                  event.registration_link ? (
                    <a href={event.registration_link} target="_blank" rel="noopener noreferrer">
                      <PrimaryButton>
                        Kayıt Ol
                      </PrimaryButton>
                    </a>
                  ) : (
                    <PrimaryButton disabled>
                      Kayıt Ol
                    </PrimaryButton>
                  )
                )}
                <GhostButton>
                  <Share2 className="h-4 w-4" /> Paylaş
                </GhostButton>
                <GhostButton>
                  <ExternalLink className="h-4 w-4" /> Takvime Ekle
                </GhostButton>
              </div>
            </div>

            {/* Right: Floating card */}
            <motion.aside
              variants={cardVariants}
              initial="initial"
              animate="animate"
              className="col-span-2 w-full lg:sticky lg:top-6 lg:self-start"
            >
              <div className="rounded-2xl bg-white p-4 sm:p-5 shadow-lg ring-1 ring-slate-200">
                <h3 className="mb-3 text-sm font-semibold text-slate-800">Etkinlik Bilgileri</h3>
                <DetailRow icon={CalendarDays} title="Tarih" value={formatDateOnly(event.start_time)} />
                <DetailRow icon={Clock} title="Saat" value={formatTimeOnly(event.start_time)} />
                <DetailRow icon={MapPin} title="Konum" value={event.location} />
                <div className="mt-4 flex gap-2">
                  {event.directions_link && (
                    <a href={event.directions_link} target="_blank" rel="noopener noreferrer">
                      <SmallButton>Yol Tarifi</SmallButton>
                    </a>
                  )}
                  {!isPast && (
                    event.registration_link ? (
                      <a href={event.registration_link} target="_blank" rel="noopener noreferrer">
                        <SmallButton variant="ghost">
                          <Link2 className="h-3.5 w-3.5" /> Bağlantı
                        </SmallButton>
                      </a>
                    ) : (
                      <SmallButton variant="ghost" disabled>
                        <Link2 className="h-3.5 w-3.5" /> Bağlantı
                      </SmallButton>
                    )
                  )}
                </div>
              </div>
            </motion.aside>
          </div>
        </motion.div>
      </section>

      {/* Content & Program */}
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-8 sm:py-12 lg:py-14">
        <div className="grid gap-6 sm:gap-8 lg:grid-cols-3 lg:gap-12">
          {/* Content */}
          <motion.section
            variants={cardVariants}
            initial="initial"
            animate="animate"
            className="lg:col-span-2"
          >
            <Card>
              <h2 className="mb-3 sm:mb-4 text-lg sm:text-xl font-semibold">Hakkında</h2>
              <div
                className="prose prose-sm sm:prose-base max-w-none prose-p:leading-relaxed prose-li:marker:text-slate-600 text-slate-700"
                dangerouslySetInnerHTML={{ __html: aboutHtml }}
              />
            </Card>

            {/* Programı sadece content içinde mevcutsa göster */}
            {programLines.length > 0 && (
              <Card className="mt-4 sm:mt-6">
                <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <h2 className="text-lg sm:text-xl font-semibold">Program Akışı</h2>
                  <div className="text-xs text-slate-500">* İçerikten parse edildi</div>
                </div>
                <Timeline start={event.start_time} end={event.end_time} contentProgramLines={programLines} />
              </Card>
            )}
          </motion.section>

          {/* Sidebar extra */}
          <motion.aside
            variants={cardVariants}
            initial="initial"
            animate="animate"
            className="lg:col-span-1"
          >
            <Card>
              <h3 className="mb-3 text-sm font-semibold text-slate-800">Organizasyon</h3>
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="grid h-8 w-8 sm:h-10 sm:w-10 place-items-center rounded-full bg-slate-200 text-base sm:text-lg font-semibold text-slate-800">
                  {(event.creator?.name || "M").slice(0, 1)}
                </div>
                <div>
                  <div className="text-sm sm:text-base font-medium text-slate-800">{event.creator?.name || "MACS"}</div>
                  <div className="text-xs text-slate-500">Oluşturan</div>
                </div>
              </div>
              <div className="mt-4 sm:mt-5 flex flex-wrap gap-2 text-xs text-slate-600">
                <Tag>#{event.slug}</Tag>
                <Tag>{event.category?.name || "Etkinlik"}</Tag>
                {event.is_active && <Tag>Aktif</Tag>}
                {event.is_featured && <Tag>Öne Çıkan</Tag>}
              </div>
            </Card>

            <Card className="mt-6">
              <h3 className="mb-3 text-sm font-semibold text-slate-800">Sık Sorulanlar</h3>
              <Accordion
                items={[
                  { q: "Katılım ücretsiz mi?", a: "Evet, etkinliğimiz öğrencilere açıktır ve ücretsizdir." },
                  { q: "Sertifika var mı?", a: "Gün sonu dijital katılım belgesi mail ile iletilecektir." },
                  { q: "Kayıt zorunlu mu?", a: "Kontenjan sınırlı olduğundan kayıt olmanızı öneririz." },
                ]}
              />
            </Card>
          </motion.aside>
        </div>

        {/* Bottom CTA (Plan A: lacivert blok) */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          className="mt-8 sm:mt-12 overflow-hidden rounded-2xl bg-[#07132b] p-4 sm:p-6 text-white"
        >
          <div className="flex flex-col items-start justify-between gap-4 sm:gap-6 md:flex-row md:items-center">
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold">{isPast ? "Bir sonraki etkinliği kaçırma" : "Bizimle aynı gün buluşalım"}</h3>
              <p className="mt-1 text-sm sm:text-base text-white/80">MACS topluluğuna katıl ve en yeni etkinliklerden haberdar ol.</p>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3 w-full md:w-auto">
              <PrimaryButton>Bildirim Al</PrimaryButton>
              <GhostButton>
                <ArrowRight className="h-4 w-4" /> Tüm Etkinlikler
              </GhostButton>
            </div>
          </div>
        </motion.div>
      </main>

      <footer className="mx-auto max-w-7xl px-4 sm:px-6 pb-12 sm:pb-16 pt-6 sm:pt-8 text-center text-xs sm:text-sm text-slate-500">
        MACS • Matematik ve Bilgisayar Bilimleri Topluluğu
      </footer>
    </div>
    </>
  );
}

/* -------------------------- UI PRIMITIVES -------------------------- */
function Card({ children, className }) {
  return (
    <div className={classNames("rounded-2xl bg-white p-6 shadow-md ring-1 ring-slate-200", className)}>
      {children}
    </div>
  );
}

function MetaChip({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-slate-200 bg-slate-50 px-2 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm text-slate-700">
      <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" /> <span className="truncate max-w-[200px] sm:max-w-none">{label}</span>
    </span>
  );
}

function PrimaryButton({ children, disabled = false }) {
  return (
    <motion.button
      whileHover={disabled ? {} : { y: -2, scale: 1.02 }}
      whileTap={disabled ? {} : { y: 0, scale: 0.98 }}
      transition={{ duration: 0.2 }}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-1.5 sm:gap-2 rounded-xl px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base font-medium shadow-md ${disabled
          ? 'bg-white/50 text-[#07132b]/50 cursor-not-allowed'
          : 'bg-white text-[#07132b] hover:shadow-xl transition-shadow duration-200'
        }`}
    >
      {children}
    </motion.button>
  );
}

function GhostButton({ children }) {
  return (
    <motion.button
      whileHover={{ y: -2, scale: 1.02 }}
      whileTap={{ y: 0, scale: 0.98 }}
      transition={{ duration: 0.2 }}
      className="inline-flex items-center justify-center gap-1.5 sm:gap-2 rounded-xl border border-white/30 bg-transparent px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base text-white hover:bg-white/10 transition-all duration-200"
    >
      {children}
    </motion.button>
  );
}

function SmallButton({ children, variant, disabled = false }) {
  const base = "inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs transition-all duration-200";
  const styles = disabled
    ? "border border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
    : variant === "ghost"
      ? "border border-slate-300 bg-transparent text-slate-700 hover:bg-slate-100 hover:scale-105"
      : "bg-[#07132b] text-white hover:bg-[#0a1a3a] hover:scale-105";
  return <button className={`${base} ${styles}`} disabled={disabled}>{children}</button>;
}

function DetailRow({ icon: Icon, title, value }) {
  return (
    <div className="flex items-start gap-3 border-b border-slate-200 py-3 last:border-none">
      <Icon className="mt-0.5 h-4 w-4 text-slate-500" />
      <div>
        <div className="text-xs text-slate-500">{title}</div>
        <div className="text-sm text-slate-800">{value}</div>
      </div>
    </div>
  );
}

function Tag({ children }) {
  return (
    <span className="inline-flex items-center justify-center rounded-full bg-slate-100 px-2.5 py-1 text-slate-700 ring-1 ring-slate-200">
      {children}
    </span>
  );
}

function Accordion({ items = [] }) {
  const [open, setOpen] = React.useState(0);
  return (
    <div>
      {items.map((it, i) => (
        <div key={i} className="border-b border-slate-200 py-3">
          <button
            type="button"
            onClick={() => setOpen(open === i ? -1 : i)}
            className="flex w-full items-center justify-between text-left text-sm font-medium"
            aria-expanded={open === i}
            aria-controls={`accordion-panel-${i}`}
          >
            <span>{it.q}</span>
            <motion.span animate={{ rotate: open === i ? 90 : 0 }}>
              <ArrowRight className="h-4 w-4" />
            </motion.span>
          </button>
          <AnimatePresence initial={false}>
            {open === i && (
              <motion.div
                key={`panel-${i}`}
                id={`accordion-panel-${i}`}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden text-sm text-slate-700"
              >
                <div className="pt-2">{it.a}</div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
}

function Timeline({ start, end, contentProgramLines }) {
  const startDate = new Date(start);
  const items = contentProgramLines && contentProgramLines.length
    ? parseProgram(contentProgramLines, startDate)
    : [];

  if (!items.length) return null;

  return (
    <ol className="relative ml-1 border-l border-slate-200 pl-6">
      {items.map((it, idx) => (
        <motion.li
          key={idx}
          initial={{ opacity: 0, x: -10 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.25, delay: idx * 0.06 }}
          className="mb-6"
        >
          <span className="absolute -left-[7px] mt-1 h-3 w-3 rounded-full bg-[#07132b]"></span>
          <div className="text-xs text-slate-500">
            {new Intl.DateTimeFormat("tr-TR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Istanbul" }).format(it.t)}
          </div>
          <div className="text-sm font-medium text-slate-800">{it.title}</div>
        </motion.li>
      ))}
    </ol>
  );
}



// Container: fetch event by slug and render view
export default function EventPageContainer() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    eventService.getBySlug(slug)
      .then(data => {
        if (mounted) {
          setEvent(data);
          setError(null);
        }
      })
      .catch(err => {
        if (mounted) {
          // API hatası durumunda mock data'dan bul
          console.log('Using mock data for event detail');
          const mockEvent = mockEvents.find(e => e.slug === slug);
          if (mockEvent) {
            setEvent(mockEvent);
            setError(null);
          } else {
            setError(err.message || 'Etkinlik bulunamadı');
          }
        }
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [slug]);

  if (loading) return <div className="spinner center"><div className="spinner-blade"></div><div className="spinner-blade"></div><div className="spinner-blade"></div><div className="spinner-blade"></div><div className="spinner-blade"></div><div className="spinner-blade"></div><div className="spinner-blade"></div><div className="spinner-blade"></div><div className="spinner-blade"></div><div className="spinner-blade"></div><div className="spinner-blade"></div><div className="spinner-blade"></div></div>;
  if (error) return <div className="error-container"><div className="error-icon">⚠️</div><p>{error}</p></div>;
  if (!event) return null;

  return <EventPageView event={event} />;
}

/* -------------------------- BASİT TESTLER -------------------------- */
function runEventPageTests() {
  try {
    // 1) Markdown başlık ayracı
    const in1 = "## Hakkında\nAçıklama\n\n## Program\n09:30 - Açılış\n10:00 - Keynote";
    const s1 = splitContent(in1);
    console.assert(s1.aboutHtml.includes("Açıklama"), "aboutHtml dönüşümü başarısız");
    console.assert(s1.programLines.length === 2, "programLines ayrıştırma başarısız");
    const p1 = parseProgram(s1.programLines, new Date("2025-10-25T09:00:00+03:00"));
    console.assert(p1[0].title.includes("Açılış"), "parseProgram başlık hatası");

    // 2) HTML başlık ayracı
    const in2 = "<h2>Hakkında</h2><p>X</p><h2>Program</h2>09:00 - Y";
    const s2 = splitContent(in2);
    console.assert(s2.programLines.length === 1, "HTML başlık ayracı başarısız");

    // 3) Düz metin 'PROGRAM:' ayracı
    const in3 = "Hakkında metin\n\nPROGRAM:\n09:15 Z";
    const s3 = splitContent(in3);
    console.assert(s3.programLines.length === 1, "PROGRAM: ayracı başarısız");

    console.log("[EventPage] Tüm testler geçti ✅");
  } catch (e) {
    console.error("[EventPage] Test hata ❌", e);
  }
}
if (typeof window !== "undefined" && !window.__EVENTPAGE_TESTED__) {
  runEventPageTests();
  window.__EVENTPAGE_TESTED__ = true;
}
